import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { Box, ButtonBase, Collapse, SxProps, Theme, Typography } from '@mui/material';

import { KeyLeverLinks } from './KeyLeverLinks';
import { type KeyLever, keyLeversStrings } from '../keyLevers.content';

export const KeyLeverCard = ({ lever }: Readonly<{ lever: KeyLever }>) => {
  const [isOpen, setIsOpen] = useState(false);
  const bodyId = `key-lever-${lever.id}-why-body`;

  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('a')) return;

    setIsOpen((open) => !open);
  };

  return (
    <Box id={`key-lever-${lever.id}`} sx={cardSx} onClick={handleCardClick}>
      <Box sx={copySx}>
        <Typography variant="text-lg-semibold" component="h3">
          {lever.name}
        </Typography>
        <Typography
          variant="text-md-regular"
          color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
        >
          {lever.directive}
        </Typography>
      </Box>
      <Box sx={whySx}>
        <ButtonBase
          id={`key-lever-${lever.id}-why-btn`}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          sx={whyButtonSx}
          disableRipple
        >
          <Typography variant="text-sm-semibold">{keyLeversStrings.whyThisMatters}</Typography>
          <AppIcon iconName={isOpen ? 'Minus' : 'Plus'} diameter="16px" color="currentColor" />
        </ButtonBase>
        <Collapse in={isOpen} unmountOnExit>
          <Box id={bodyId} sx={bodySx}>
            <Typography
              variant="text-sm-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
            >
              {lever.rationale}
            </Typography>
            <KeyLeverLinks lever={lever} />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  p: backrs.spacing['spacing-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  cursor: 'pointer',
  transition:
    'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out, border-color 0.3s ease-in-out',
  '&:hover, &:focus-within': {
    borderColor: backrs.colors['Colors/Base/Black'],
    boxShadow: backrs.shadows['shadow-lg'],
    transform: 'translate(-2px, -2px)',
  },
});

const copySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const whySx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  pt: backrs.spacing['spacing-md'],
});

const whyButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  borderRadius: backrs.radius['radius-sm'],
  transition: 'color 200ms linear',
  '&:hover': { color: backrs.colors['Brand/600'] },
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  pt: backrs.spacing['spacing-md'],
});
